import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Checkbox, FormControl, IconButton, Menu, MenuItem, Switch, TableCell, TableRow, Tooltip } from "@mui/material";
import { TableRecipeFragment, useOrgRecipesTableQuery } from "apps/web/src/types";
import { useUser } from "apps/web/src/utils/tokens";
import { useEffect } from "react";
import { useDateFormatting } from "shared-ui/contexts/Locale/useDateFormatting";
import { Score } from "shared-ui/Score/Score";
import { AiAugmentedRecipeName } from "../AiAugmentedRecipeName";
import { useRecipesPage } from "../useRecipesPage";

const EMPTY_ROWS: TableRecipeFragment[] = [];

const MoreInfoMenu = () => {
  const user = useUser();

  const {
    setMoreInfo,
    moreInfo,
    onDuplicate,
    setDeleteableRecipe,
    setPrintRecipeId,
    setMoveToOrgGroupRecipe,
    setSaveAsMenuItemRecipeId,
    hasOrgGroupResourceSharing,
  } = useRecipesPage();

  if (!moreInfo) {
    return null;
  }

  return (
    <Menu
      anchorEl={moreInfo?.moreAnchorElement}
      open={!!moreInfo}
      onClose={() => {
        setMoreInfo(null);
      }}
      keepMounted
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <MenuItem
        dense
        onClick={() => {
          onDuplicate(moreInfo.recipe.id);
          setMoreInfo(null);
        }}
      >
        Duplicate
      </MenuItem>
      <MenuItem
        dense
        onClick={() => {
          setDeleteableRecipe(moreInfo.recipe);
          setMoreInfo(null);
        }}
      >
        Delete
      </MenuItem>
      <MenuItem
        dense
        onClick={() => {
          setPrintRecipeId(moreInfo.recipe.id);
          setMoreInfo(null);
        }}
      >
        Print
      </MenuItem>
      <MenuItem
        dense
        onClick={() => {
          setSaveAsMenuItemRecipeId(moreInfo.recipe.id);
          setMoreInfo(null);
        }}
      >
        Save as Menu Item
      </MenuItem>
      {user?.orgMembership?.isAdmin && hasOrgGroupResourceSharing && (
        <MenuItem
          dense
          onClick={() => {
            setMoveToOrgGroupRecipe(moreInfo.recipe);
            setMoreInfo(null);
          }}
        >
          Move to Org Group
        </MenuItem>
      )}
    </Menu>
  );
};

export const OrgRecipeRows = () => {
  const { formatDateWithLocale } = useDateFormatting();
  const {
    paginationHooks,
    handleEditIsShared,
    scoringSystem,
    createdById,
    selectedMealTypes,
    onEditRecipe,
    setMoreInfo,
    setTotalRows,
    setRowsLoading,
    isBulkDeleteRecipesEnabled,
    selectedRecipeIds,
    toggleRecipeSelection,
    setVisibleRecipeIds,
  } = useRecipesPage();
  const { limit, offset, query } = paginationHooks;
  const user = useUser();

  const { data: recipeData, loading: recipeLoading } = useOrgRecipesTableQuery({
    variables: {
      input: { limit, offset },
      sort: null,
      query,
      createdById,
      mealTypes: selectedMealTypes.length > 0 ? selectedMealTypes : null,
      orgId: user?.orgMembership?.org.id || null,
    },
  });

  useEffect(() => {
    setRowsLoading(recipeLoading);
  }, [recipeLoading, setRowsLoading]);

  const total = recipeData?.recipeOffsetConnection.pageInfo.total;

  // Derive total from data (not onCompleted): after a bulk/single delete, resetTable
  // evicts the cache while already on page 0, so variables don't change and onCompleted
  // would not re-fire — leaving the count stale even though rows refresh.
  useEffect(() => {
    if (total !== undefined) {
      setTotalRows(total);
    }
  }, [total, setTotalRows]);

  const rows = recipeData?.recipeOffsetConnection.edges || EMPTY_ROWS;

  useEffect(() => {
    setVisibleRecipeIds(rows.map(r => r.id));
  }, [rows, setVisibleRecipeIds]);

  return (
    <>
      {rows.map(row => {
        const { id, name, isShared, createdBy, mealTypes, servings, score, isAiGenerated, isAiTranscribed, createdAt, updatedAt } = row;
        const aiAugmented = isAiGenerated || isAiTranscribed;
        const isSelected = selectedRecipeIds.has(id);
        return (
          <TableRow
            key={id}
            id={id}
            hover
            selected={isSelected}
            sx={{
              cursor: "pointer",
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: "primary.main",
                outlineOffset: "-2px",
              },
            }}
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onEditRecipe(row);
              }
            }}
            onClick={() => onEditRecipe(row)}
          >
            {isBulkDeleteRecipesEnabled && (
              <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                <Checkbox
                  checked={isSelected}
                  onClick={e => {
                    e.stopPropagation();
                    toggleRecipeSelection(id);
                  }}
                  inputProps={{ "aria-label": `Select ${name}` }}
                />
              </TableCell>
            )}
            <TableCell>
              <Tooltip title={"Share recipe with athletes"}>
                <FormControl>
                  <Switch
                    size="medium"
                    inputProps={{
                      "aria-label": "Share recipe with athletes",
                    }}
                    checked={isShared}
                    onClick={e => e.stopPropagation()}
                    onChange={e => {
                      handleEditIsShared(id, e.target.checked);
                    }}
                  />
                </FormControl>
              </Tooltip>
            </TableCell>
            {aiAugmented ? (
              <TableCell>
                <AiAugmentedRecipeName
                  name={name}
                  sx={{ fontSize: "14px" }}
                  isAiGenerated={isAiGenerated}
                  isAiTranscribed={isAiTranscribed}
                />
              </TableCell>
            ) : (
              <TableCell>{name}</TableCell>
            )}
            <TableCell>
              {createdBy.firstName} {createdBy.lastName}
            </TableCell>
            <TableCell>
              {mealTypes &&
                mealTypes
                  .map(mt => {
                    const mealTypeString = mt.toString();
                    return mealTypeString.charAt(0).toUpperCase() + mealTypeString.slice(1);
                  })
                  .sort()
                  .join(", ")}
            </TableCell>
            <TableCell>{servings.map(s => `${s.perRecipeYield} ${s.units}`).join(", ")}</TableCell>
            {scoringSystem && (
              <TableCell>
                {score && (
                  <>
                    {" "}
                    <Score scoreValue={score.value} scoringSystem={scoringSystem} sx={{ gap: 0.5 }} size="lg" />
                  </>
                )}
              </TableCell>
            )}
            <TableCell>{formatDateWithLocale(new Date(createdAt))}</TableCell>
            <TableCell>{formatDateWithLocale(new Date(updatedAt))}</TableCell>
            <TableCell>
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  setMoreInfo({ moreAnchorElement: e.currentTarget, recipe: row });
                }}
                onKeyDown={event => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    event.stopPropagation();
                    setMoreInfo({ moreAnchorElement: event.currentTarget, recipe: row });
                  }
                }}
                role="button"
                aria-label="More Actions"
                tabIndex={0}
              >
                <MoreVertIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        );
      })}
      <MoreInfoMenu />
    </>
  );
};
